import { useState, useMemo } from 'react';
import { X, Search, Loader2, Baby, CheckCircle } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import type { Pet } from '../../types/pet';
import { usePets } from '../../context/PetsContext';
import {
  CreateBirthEventRequest,
  CreatePuppyInput,
  ParentRole,
  createBirthEventAndPuppies,
  searchPetByDid,
  type SearchPetsResponse,
} from '../../../../api/pets';
import { usePetPhoto } from '../../hooks/usePetPhoto';
import { toast } from 'sonner';

const MAX_LITTER_SIZE = 20;

interface RecordBirthModalProps {
  pet: Pet;
  /** When opened from a parent DID card, pre-select Mother/Father so user does not have to choose. */
  initialParentRole?: 'mother' | 'father';
  onClose: () => void;
  onSuccess: () => void;
}

export function RecordBirthModal({
  pet,
  initialParentRole,
  onClose,
  onSuccess,
}: RecordBirthModalProps) {
  const { t } = useTranslation();
  const { refreshPets } = usePets();
  const [step, setStep] = useState<1 | 2>(1);

  // Step 1 state – parent role pre-selected when opened from a parent DID card
  const [birthDate, setBirthDate] = useState('');
  const [litterSize, setLitterSize] = useState<number>(1);
  const [parentRole, setParentRole] = useState<ParentRole>(
    initialParentRole ?? 'mother'
  );
  const [otherParentDid, setOtherParentDid] = useState('');
  const [otherParentSearchResult, setOtherParentSearchResult] = useState<
    SearchPetsResponse['pets'][0] | null
  >(null);
  const [searchingDid, setSearchingDid] = useState(false);
  const [location, setLocation] = useState('');
  const [notes, setNotes] = useState('');
  const [step1Errors, setStep1Errors] = useState<Record<string, string>>({});

  // Step 2 state – puppy cards
  const [puppies, setPuppies] = useState<CreatePuppyInput[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  const parentDid = pet.did || '';

  const initPuppies = useMemo(() => {
    const list: CreatePuppyInput[] = [];
    for (let i = 0; i < litterSize; i++) {
      list.push({
        name: `Puppy #${i + 1}`,
        gender: 'male',
        color: '',
        notes: '',
        ownershipStatus: 'active',
      });
    }
    return list;
  }, [litterSize]);

  const handleLitterSizeChange = (value: number) => {
    const n = Math.min(MAX_LITTER_SIZE, Math.max(1, value));
    setLitterSize(n);
    setPuppies((prev) => {
      if (prev.length === n) return prev;
      if (prev.length > n) return prev.slice(0, n);
      const next = [...prev];
      for (let i = prev.length; i < n; i++) {
        next.push({
          name: `Puppy #${i + 1}`,
          gender: 'male',
          color: '',
          notes: '',
          ownershipStatus: 'active',
        });
      }
      return next;
    });
  };

  const handleSearchByDid = async () => {
    const did = otherParentDid.trim();
    if (!did) {
      setOtherParentSearchResult(null);
      return;
    }
    setSearchingDid(true);
    setOtherParentSearchResult(null);
    try {
      const res = await searchPetByDid(did);
      if (res.pets?.length > 0) {
        setOtherParentSearchResult(res.pets[0]);
      } else {
        setOtherParentSearchResult(null);
        toast.info(t('family.birth.noPetForDid', 'No pet found for this DID.'));
      }
    } catch {
      setOtherParentSearchResult(null);
      toast.error(t('common.error', 'Error searching by DID.'));
    } finally {
      setSearchingDid(false);
    }
  };

  const validateStep1 = (): boolean => {
    const err: Record<string, string> = {};
    if (!birthDate.trim()) {
      err.birthDate = t(
        'family.birth.dateRequired',
        'Date of birth is required.'
      );
    } else {
      const d = new Date(birthDate);
      if (d > new Date()) {
        err.birthDate = t(
          'family.birth.dateNotFuture',
          'Birth date cannot be in the future.'
        );
      }
    }
    if (litterSize < 1 || litterSize > MAX_LITTER_SIZE) {
      err.litterSize = t(
        'family.birth.litterSizeInvalid',
        `Enter between 1 and ${MAX_LITTER_SIZE}.`
      );
    }
    setStep1Errors(err);
    return Object.keys(err).length === 0;
  };

  const handleStep1Next = () => {
    if (!validateStep1()) return;
    setPuppies(initPuppies);
    setStep(2);
  };

  const updatePuppy = (index: number, updates: Partial<CreatePuppyInput>) => {
    setPuppies((prev) =>
      prev.map((p, i) => (i === index ? { ...p, ...updates } : p))
    );
  };

  const handleConfirmBirth = async () => {
    setSubmitting(true);
    try {
      const request: CreateBirthEventRequest = {
        birthDate,
        litterSize,
        parentRole,
        parentPrimaryDid: parentDid,
        parentSecondaryDid:
          otherParentSearchResult?.did || otherParentDid.trim() || undefined,
        location: location.trim() || undefined,
        notes: notes.trim() || undefined,
      };
      await createBirthEventAndPuppies(
        pet.id,
        parentDid,
        request,
        puppies,
        pet.species,
        pet.breed || 'Unknown'
      );
      setSuccess(true);
      await refreshPets();
      toast.success(
        t('family.birth.success', 'Birth registered and puppies created.')
      );
      setTimeout(() => {
        onSuccess();
        onClose();
      }, 1500);
    } catch (e) {
      console.error(e);
      toast.error(
        t('family.birth.error', 'Failed to register birth. Please try again.')
      );
    } finally {
      setSubmitting(false);
    }
  };

  const { signedImageUrl } = usePetPhoto({
    petId: otherParentSearchResult?.id || '',
    imageUrl: otherParentSearchResult?.imageUrl,
  });

  if (success) {
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
        <div className="bg-white dark:bg-[#16151D] rounded-xl shadow-2xl border dark:border-white/8 max-w-md w-full p-8 text-center animate-in zoom-in-95 duration-300">
          <div className="w-16 h-16 rounded-full bg-green-100 dark:bg-green-900/40 flex items-center justify-center mx-auto mb-4">
            <CheckCircle className="w-10 h-10 text-green-600 dark:text-green-400" />
          </div>
          <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
            {t('family.birth.celebrate', 'Birth registered!')}
          </h3>
          <p className="text-sm text-gray-600 dark:text-white/70">
            {t(
              'family.birth.puppiesCreated',
              'Puppies have been added to the family tree.'
            )}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-[#16151D] rounded-xl shadow-2xl border dark:border-white/8 max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        <div className="bg-gradient-to-r from-amber-500/90 to-pink-500/90 dark:from-amber-600 dark:to-pink-600 text-white p-6 flex-shrink-0">
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-2">
              <Baby className="w-7 h-7" />
              <div>
                <h2 className="text-xl font-bold">
                  {step === 1
                    ? t('family.birth.recordBirth', 'Record Birth / Litter')
                    : t('family.birth.puppyDetails', 'Puppy details')}
                </h2>
                <p className="text-sm opacity-90">
                  {pet.name} –{' '}
                  {step === 1
                    ? t('family.birth.step1Subtitle', 'Birth event')
                    : t('family.birth.step2Subtitle', 'Confirm puppies')}
                </p>
              </div>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="text-white/80 hover:text-white p-1 rounded"
            >
              <X className="w-6 h-6" />
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-6">
          {step === 1 && (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-white/90 mb-1">
                  {t('family.birth.dateOfBirth', 'Date of birth')} *
                </label>
                <input
                  type="date"
                  value={birthDate}
                  onChange={(e) => setBirthDate(e.target.value)}
                  max={new Date().toISOString().slice(0, 10)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-white/20 rounded-lg bg-white dark:bg-[#0F0E16] text-gray-900 dark:text-white"
                />
                {step1Errors.birthDate && (
                  <p className="text-sm text-red-500 mt-1">
                    {step1Errors.birthDate}
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-white/90 mb-1">
                  {t('family.birth.numberOfPuppies', 'Number of puppies')} *
                </label>
                <input
                  type="number"
                  min={1}
                  max={MAX_LITTER_SIZE}
                  value={litterSize}
                  onChange={(e) =>
                    handleLitterSizeChange(parseInt(e.target.value, 10) || 1)
                  }
                  className="w-full px-3 py-2 border border-gray-300 dark:border-white/20 rounded-lg bg-white dark:bg-[#0F0E16] text-gray-900 dark:text-white"
                />
                {step1Errors.litterSize && (
                  <p className="text-sm text-red-500 mt-1">
                    {step1Errors.litterSize}
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-white/90 mb-2">
                  {t('family.birth.thisPetIs', 'This pet is')}
                </label>
                <div className="flex gap-4">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="parentRole"
                      checked={parentRole === 'mother'}
                      onChange={() => setParentRole('mother')}
                      className="rounded-full"
                    />
                    <span className="text-gray-900 dark:text-white">
                      {t('family.birth.mother', 'Mother')}
                    </span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="parentRole"
                      checked={parentRole === 'father'}
                      onChange={() => setParentRole('father')}
                      className="rounded-full"
                    />
                    <span className="text-gray-900 dark:text-white">
                      {t('family.birth.father', 'Father')}
                    </span>
                  </label>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-white/90 mb-1">
                  {t('family.birth.otherParent', 'Other parent (optional)')}
                </label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={otherParentDid}
                    onChange={(e) => {
                      setOtherParentDid(e.target.value);
                      setOtherParentSearchResult(null);
                    }}
                    placeholder={t('family.birth.searchByPetDid')}
                    className="flex-1 px-3 py-2 border border-gray-300 dark:border-white/20 rounded-lg bg-white dark:bg-[#0F0E16] text-gray-900 dark:text-white font-mono text-sm"
                  />
                  <button
                    type="button"
                    onClick={handleSearchByDid}
                    disabled={searchingDid || !otherParentDid.trim()}
                    className="px-4 py-2 rounded-lg bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2"
                  >
                    {searchingDid ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Search className="w-4 h-4" />
                    )}
                    {t('common.search', 'Search')}
                  </button>
                </div>
                {otherParentSearchResult && (
                  <div className="mt-2 p-3 rounded-lg bg-gray-50 dark:bg-[#0F0E16] border border-gray-200 dark:border-white/10 flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full overflow-hidden bg-gray-200 dark:bg-gray-700">
                      {signedImageUrl ? (
                        <img
                          src={signedImageUrl}
                          alt=""
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <span className="flex items-center justify-center w-full h-full text-lg">
                          {otherParentSearchResult.species === 'dog'
                            ? '🐕'
                            : '🐈'}
                        </span>
                      )}
                    </div>
                    <div>
                      <div className="font-medium text-gray-900 dark:text-white">
                        {otherParentSearchResult.name}
                      </div>
                      <div className="text-sm text-gray-500 dark:text-white/60">
                        {otherParentSearchResult.breed}
                      </div>
                      <div className="font-mono text-xs text-gray-400 dark:text-white/50 truncate max-w-[200px]">
                        {otherParentSearchResult.did}
                      </div>
                    </div>
                  </div>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-white/90 mb-1">
                  {t('family.birth.location', 'Location')} (
                  {t('common.optional', 'Optional')})
                </label>
                <input
                  type="text"
                  value={location}
                  onChange={(e) => setLocation(e.target.value)}
                  placeholder={t('family.birth.locationPlaceholder')}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-white/20 rounded-lg bg-white dark:bg-[#0F0E16] text-gray-900 dark:text-white"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-white/90 mb-1">
                  {t('family.birth.notes', 'Notes')} (
                  {t('common.optional', 'Optional')})
                </label>
                <textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  rows={2}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-white/20 rounded-lg bg-white dark:bg-[#0F0E16] text-gray-900 dark:text-white resize-none"
                />
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={onClose}
                  className="flex-1 py-2.5 border border-gray-300 dark:border-white/20 rounded-lg text-gray-700 dark:text-white/90 hover:bg-gray-50 dark:hover:bg-white/5"
                >
                  {t('common.cancel', 'Cancel')}
                </button>
                <button
                  type="button"
                  onClick={handleStep1Next}
                  className="flex-1 py-2.5 rounded-lg bg-gradient-to-r from-amber-500 to-pink-500 text-white font-medium hover:opacity-90"
                >
                  {t('common.next', 'Next')}
                </button>
              </div>
            </div>
          )}

          {step === 2 && (
            <div className="space-y-4">
              <p className="text-sm text-gray-600 dark:text-white/70">
                {t(
                  'family.birth.editPuppyDetails',
                  'Edit details for each puppy. A unique Pet DID will be assigned when created.'
                )}
              </p>
              <div className="space-y-3 max-h-[50vh] overflow-y-auto pr-1">
                {puppies.map((puppy, index) => (
                  <div
                    key={index}
                    className="p-4 rounded-xl border border-gray-200 dark:border-white/10 bg-gray-50/50 dark:bg-white/5 space-y-3"
                  >
                    <div className="font-medium text-gray-900 dark:text-white">
                      {t('family.birth.puppyNumber', 'Puppy')} #{index + 1}
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs text-gray-500 dark:text-white/50 mb-1">
                          {t('family.birth.name', 'Name')}
                        </label>
                        <input
                          type="text"
                          value={puppy.name}
                          onChange={(e) =>
                            updatePuppy(index, { name: e.target.value })
                          }
                          className="w-full px-2 py-1.5 text-sm border border-gray-300 dark:border-white/20 rounded bg-white dark:bg-[#0F0E16] text-gray-900 dark:text-white"
                        />
                      </div>
                      <div>
                        <label className="block text-xs text-gray-500 dark:text-white/50 mb-1">
                          {t('family.birth.gender', 'Gender')}
                        </label>
                        <select
                          value={puppy.gender}
                          onChange={(e) =>
                            updatePuppy(index, {
                              gender: e.target.value as 'male' | 'female',
                            })
                          }
                          className="w-full px-2 py-1.5 text-sm border border-gray-300 dark:border-white/20 rounded bg-white dark:bg-[#0F0E16] text-gray-900 dark:text-white"
                        >
                          <option value="male">
                            {t('family.birth.male', 'Male')}
                          </option>
                          <option value="female">
                            {t('family.birth.female', 'Female')}
                          </option>
                        </select>
                      </div>
                    </div>
                    <div>
                      <label className="block text-xs text-gray-500 dark:text-white/50 mb-1">
                        {t('family.birth.colorMarkings', 'Color / markings')}
                      </label>
                      <input
                        type="text"
                        value={puppy.color || ''}
                        onChange={(e) =>
                          updatePuppy(index, { color: e.target.value })
                        }
                        className="w-full px-2 py-1.5 text-sm border border-gray-300 dark:border-white/20 rounded bg-white dark:bg-[#0F0E16] text-gray-900 dark:text-white"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-500 dark:text-white/50 mb-1">
                        {t('family.birth.notes', 'Notes')}
                      </label>
                      <input
                        type="text"
                        value={puppy.notes || ''}
                        onChange={(e) =>
                          updatePuppy(index, { notes: e.target.value })
                        }
                        className="w-full px-2 py-1.5 text-sm border border-gray-300 dark:border-white/20 rounded bg-white dark:bg-[#0F0E16] text-gray-900 dark:text-white"
                      />
                    </div>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={puppy.ownershipStatus === 'pending_transfer'}
                        onChange={(e) =>
                          updatePuppy(index, {
                            ownershipStatus: e.target.checked
                              ? 'pending_transfer'
                              : 'active',
                          })
                        }
                      />
                      <span className="text-sm text-gray-700 dark:text-white/80">
                        {t(
                          'family.birth.pendingOwnershipTransfer',
                          'Pending ownership transfer'
                        )}
                      </span>
                    </label>
                  </div>
                ))}
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setStep(1)}
                  className="flex-1 py-2.5 border border-gray-300 dark:border-white/20 rounded-lg text-gray-700 dark:text-white/90 hover:bg-gray-50 dark:hover:bg-white/5"
                >
                  {t('common.back', 'Back')}
                </button>
                <button
                  type="button"
                  onClick={handleConfirmBirth}
                  disabled={submitting}
                  className="flex-1 py-2.5 rounded-lg bg-gradient-to-r from-amber-500 to-pink-500 text-white font-medium hover:opacity-90 disabled:opacity-70 flex items-center justify-center gap-2"
                >
                  {submitting ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin" />
                      {t('common.saving', 'Saving...')}
                    </>
                  ) : (
                    t(
                      'family.birth.confirmBirthCreatePuppies',
                      'Confirm Birth & Create Puppies'
                    )
                  )}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
